import type { OverviewAnalytics } from '../hooks/useOverviewAnalytics';
import type { EnterpriseAnalytics } from '../hooks/useEnterpriseAnalytics';
import type { TimeRange } from '../components/reports/TimeRangeSelector';
import type { ForecastData } from './analyticsCalculations';

/**
 * Capture a DOM element as PDF - exact visual replica including charts, colors, layout.
 * Uses html2canvas + jsPDF for WYSIWYG export.
 */
export async function captureElementToPDF(element: HTMLElement, filename: string): Promise<void> {
  const html2canvas = (await import('html2canvas')).default;
  const { jsPDF } = await import('jspdf');

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    allowTaint: true,
    imageTimeout: 0,
  });

  const pdf = new jsPDF({
    orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgRatio = canvas.width / canvas.height;
  const imgWidth = pageWidth;
  const imgHeight = pageWidth / imgRatio;

  if (imgHeight <= pageHeight) {
    const yOffset = (pageHeight - imgHeight) / 2;
    pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', 0, yOffset, imgWidth, imgHeight);
  } else {
    const totalPages = Math.ceil((imgHeight / pageHeight));
    let heightLeft = imgHeight;
    let srcY = 0;
    for (let i = 0; i < totalPages; i++) {
      if (i > 0) pdf.addPage();
      const pageCanvas = document.createElement('canvas');
      const pageHeightPx = (pageHeight / imgHeight) * canvas.height;
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.min(pageHeightPx, canvas.height - srcY);
      const ctx = pageCanvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, srcY, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height);
      const sliceHeight = (pageCanvas.height / canvas.height) * imgHeight;
      pdf.addImage(pageCanvas.toDataURL('image/png', 1.0), 'PNG', 0, 0, imgWidth, sliceHeight);
      heightLeft -= pageHeight;
      srcY += pageCanvas.height;
    }
  }

  pdf.save(filename);
}

function colToLetter(colIndex: number): string {
  let result = '';
  let n = colIndex;
  while (n > 0) {
    const rem = (n - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}

function formatCurrency(value: number): string {
  return `€${value.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getUnsoldItems(overview: OverviewAnalytics, soldNamesArray: string[]) {
  const soldNames = new Set(soldNamesArray);
  return overview.sales.allProducts
    .filter(p => !soldNames.has(p.name))
    .sort((a, b) => a.quantity - b.quantity)
    .slice(0, 10);
}

/**
 * Export full analytics to Excel (4 sheets: Overview, Profit, Products, Forecasting)
 */
export async function exportAnalyticsToExcel(
  overview: OverviewAnalytics,
  enterprise: EnterpriseAnalytics,
  timeRange: TimeRange
): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Enterprise Analytics';
  workbook.created = new Date();

  const dateStr = new Date().toISOString().split('T')[0];
  const rangeLabel = timeRange.label || timeRange.value;

  // --- Sheet 1: Overview ---
  const wsOverview = workbook.addWorksheet('Overview', {
    properties: { tabColor: { argb: '0EA5E9' } },
    pageSetup: { orientation: 'portrait', fitToPage: true },
  });
  wsOverview.columns = [
    { key: 'metric', width: 28 },
    { key: 'value', width: 18 },
  ];
  const lastColOverview = colToLetter(2);
  wsOverview.mergeCells(`A1:${lastColOverview}1`);
  const titleRow = wsOverview.addRow(['ENTERPRISE ANALYTICS REPORT']);
  titleRow.height = 28;
  titleRow.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '0C4A6E' } };
  titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F9FF' } };
  wsOverview.addRow([`Generated: ${new Date().toLocaleString()}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  wsOverview.addRow([`Time Range: ${rangeLabel}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  wsOverview.addRow([]);

  const overviewHeader = wsOverview.addRow(['Metric', 'Value']);
  overviewHeader.height = 24;
  overviewHeader.eachCell((cell) => {
    cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0EA5E9' } };
    cell.alignment = { vertical: 'middle' };
  });
  const overviewData = [
    ['Online Revenue', overview.sales.onlineRevenue],
    ['Shop Revenue', overview.sales.shopRevenue],
    ['Combined Revenue', overview.sales.totalRevenue],
    ['Avg Order Value', overview.sales.avgOrderValue],
    ['Total Orders', overview.sales.totalOrders],
    ['Today Revenue', overview.sales.revToday],
    ['This Week Revenue', overview.sales.revWeek],
    ['This Month Revenue', overview.sales.revMonth],
    ['Today Items Sold', overview.sales.qtyToday],
    ['Week Items Sold', overview.sales.qtyWeek],
    ['Month Items Sold', overview.sales.qtyMonth],
    ['Total Inventory Value', overview.inventory.totalValue],
    ['Total Items', overview.inventory.totalItems],
    ['Total Quantity', overview.inventory.totalQuantity],
    ['Low Stock Count', overview.inventory.lowStockCount],
    ['Out of Stock Count', overview.inventory.outOfStockCount],
    ['Fulfillment Rate', overview.performance.fulfillmentRate],
    ['Inventory Turnover', overview.performance.inventoryTurnover],
    ['Categories', overview.inventory.categories],
  ];
  overviewData.forEach(([metric, val], i) => {
    const row = wsOverview.addRow([metric, typeof val === 'number' ? val : String(val)]);
    row.getCell(1).font = { name: 'Arial', size: 10, color: { argb: '334155' } };
    row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F8FAFC' } };
    const valCell = row.getCell(2);
    valCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F8FAFC' } };
    if (typeof val === 'number') {
      const m = String(metric);
      if (m.includes('Rate') || m.includes('Turnover')) valCell.numFmt = '0.00';
      else if (m.includes('Value') || m.includes('Revenue') || m.includes('Order Value')) valCell.numFmt = '"EUR "#,##0.00';
    }
    valCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '0C4A6E' } };
  });

  wsOverview.addRow([]);
  const unsoldHeader = wsOverview.addRow(['MOST UNSOLD ITEMS']);
  unsoldHeader.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: '64748B' } };
  const fullUnsoldPeriods: Array<{ label: string; soldNames: string[] }> = [
    { label: 'Today', soldNames: overview.sales.soldNamesToday || [] },
    { label: 'Week', soldNames: overview.sales.soldNamesWeek || [] },
    { label: 'Month', soldNames: overview.sales.soldNamesMonth || [] },
    { label: 'All Time', soldNames: overview.sales.soldNamesAll || [] },
  ];
  for (const period of fullUnsoldPeriods) {
    const periodRow = wsOverview.addRow([`Unsold - ${period.label}`]);
    periodRow.getCell(1).font = { name: 'Arial', size: 10, bold: true, color: { argb: '475569' } };
    const unsold = getUnsoldItems(overview, period.soldNames);
    unsold.forEach((item, i) => {
      const row = wsOverview.addRow([`  ${i + 1}. ${item.name}`, item.quantity]);
      row.getCell(1).font = { name: 'Arial', size: 10, color: { argb: '334155' } };
      row.getCell(2).font = { name: 'Arial', size: 10, color: { argb: '64748B' } };
    });
  }

  // --- Sheet 2: Profit ---
  const wsProfit = workbook.addWorksheet('Profit', {
    properties: { tabColor: { argb: '059669' } },
    pageSetup: { orientation: 'portrait', fitToPage: true },
  });
  wsProfit.columns = [
    { key: 'metric', width: 28 },
    { key: 'value', width: 18 },
  ];
  const lastColProfit = colToLetter(2);
  wsProfit.mergeCells(`A1:${lastColProfit}1`);
  const profitTitle = wsProfit.addRow(['PROFIT ANALYSIS']);
  profitTitle.height = 28;
  profitTitle.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '065F46' } };
  profitTitle.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  profitTitle.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D1FAE5' } };
  wsProfit.addRow([`Time Range: ${rangeLabel}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  wsProfit.addRow([]);

  const profitHeader = wsProfit.addRow(['Metric', 'Value']);
  profitHeader.eachCell((cell) => {
    cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '059669' } };
  });
  const profit = enterprise.profit;
  const profitData = [
    ['Gross Profit', profit.grossProfit],
    ['Gross Margin (%)', profit.grossMargin],
    ['Net Profit', profit.netProfit],
    ['Net Margin (%)', profit.netMargin],
    ['Cost of Goods Sold', profit.costOfGoodsSold],
  ];
  profitData.forEach(([metric, val], i) => {
    const row = wsProfit.addRow([metric, val]);
    row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0FDF4' } };
    const valCell = row.getCell(2);
    valCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0FDF4' } };
    valCell.numFmt = String(metric).includes('Margin') ? '0.00' : '"EUR "#,##0.00';
    valCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '065F46' } };
  });

  if (profit.profitTrend && profit.profitTrend.length > 0) {
    wsProfit.addRow([]);
    const trendHeader = wsProfit.addRow(['Date', 'Daily Profit']);
    trendHeader.eachCell((cell) => {
      cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '047857' } };
    });
    profit.profitTrend.forEach((pt, i) => {
      const row = wsProfit.addRow([pt.date, pt.value]);
      row.getCell(2).numFmt = '"EUR "#,##0.00';
      row.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0FDF4' } };
    });
  }

  // --- Sheet 3: Products ---
  const wsProducts = workbook.addWorksheet('Products', {
    properties: { tabColor: { argb: '7C3AED' } },
    pageSetup: { orientation: 'landscape', fitToPage: true },
  });
  wsProducts.columns = [
    { key: 'rank', width: 6 },
    { key: 'name', width: 32 },
    { key: 'revenue', width: 14 },
    { key: 'profit', width: 14 },
    { key: 'quantity', width: 10 },
    { key: 'color', width: 12 },
    { key: 'size', width: 10 },
  ];
  const lastColProducts = colToLetter(7);
  wsProducts.mergeCells(`A1:${lastColProducts}1`);
  const productsTitle = wsProducts.addRow(['PRODUCT PERFORMANCE']);
  productsTitle.height = 28;
  productsTitle.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '5B21B6' } };
  productsTitle.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  productsTitle.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EDE9FE' } };
  wsProducts.addRow([`Time Range: ${rangeLabel}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  wsProducts.addRow([]);

  const topHeader = wsProducts.addRow(['TOP 10 PRODUCTS BY REVENUE']);
  wsProducts.mergeCells('A' + topHeader.number + ':G' + topHeader.number);
  topHeader.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
  topHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '059669' } };
  topHeader.getCell(1).alignment = { horizontal: 'center' };
  const prodCols = ['Rank', 'Product Name', 'Revenue', 'Profit', 'Quantity', 'Color', 'Size'];
  const prodHeaderRow = wsProducts.addRow(prodCols);
  prodHeaderRow.eachCell((cell) => {
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '7C3AED' } };
  });
  enterprise.products.topProducts.slice(0, 10).forEach((p, i) => {
    const row = wsProducts.addRow([
      i + 1,
      p.name,
      p.revenue,
      p.profit,
      p.quantity,
      p.colorName || '-',
      p.sizeName || '-',
    ]);
    row.getCell(3).numFmt = '"EUR "#,##0.00';
    row.getCell(4).numFmt = '"EUR "#,##0.00';
    row.getCell(5).alignment = { horizontal: 'center' };
    row.eachCell((cell, col) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F5F3FF' } };
    });
  });

  wsProducts.addRow([]);
  const bottomHeader = wsProducts.addRow(['BOTTOM 10 PRODUCTS']);
  wsProducts.mergeCells('A' + bottomHeader.number + ':G' + bottomHeader.number);
  bottomHeader.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
  bottomHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DC2626' } };
  bottomHeader.getCell(1).alignment = { horizontal: 'center' };
  wsProducts.addRow(prodCols).eachCell((cell) => {
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'B91C1C' } };
  });
  enterprise.products.bottomProducts.slice(0, 10).forEach((p, i) => {
    const row = wsProducts.addRow([
      i + 1,
      p.name,
      p.revenue,
      p.profit,
      p.quantity,
      p.colorName || '-',
      p.sizeName || '-',
    ]);
    row.getCell(3).numFmt = '"EUR "#,##0.00';
    row.getCell(4).numFmt = '"EUR "#,##0.00';
    row.getCell(5).alignment = { horizontal: 'center' };
    row.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'FEF2F2' } };
    });
  });

  wsProducts.addRow([]);
  const abcHeader = wsProducts.addRow(['ABC ANALYSIS']);
  wsProducts.mergeCells('A' + abcHeader.number + ':D' + abcHeader.number);
  abcHeader.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
  abcHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0369A1' } };
  abcHeader.getCell(1).alignment = { horizontal: 'center' };
  wsProducts.addRow(['Category', 'Product Name', 'Value', 'Percentage']).eachCell((cell) => {
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0284C7' } };
  });
  const abcColors = { A: '059669', B: 'D97706', C: '64748B' };
  for (const cat of ['A', 'B', 'C'] as const) {
    const prods = enterprise.products.abcAnalysis[cat]?.products ?? [];
    prods.forEach((p, i) => {
      const row = wsProducts.addRow([cat, p.name, p.value, p.percentage]);
      row.getCell(3).numFmt = '"EUR "#,##0.00';
      row.getCell(4).numFmt = '0.00%';
      row.eachCell((cell, col) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0F9FF' } };
      });
      row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: abcColors[cat] } };
      row.getCell(1).font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    });
  }

  // --- Sheet 4: Forecasting ---
  const wsForecast = workbook.addWorksheet('Forecasting', {
    properties: { tabColor: { argb: '0284C7' } },
    pageSetup: { orientation: 'portrait', fitToPage: true },
  });
  wsForecast.columns = [
    { key: 'date', width: 14 },
    { key: 'value', width: 16 },
    { key: 'lower', width: 16 },
    { key: 'upper', width: 16 },
  ];
  const lastColForecast = colToLetter(4);
  wsForecast.mergeCells(`A1:${lastColForecast}1`);
  const forecastTitle = wsForecast.addRow(['REVENUE FORECAST']);
  forecastTitle.height = 28;
  forecastTitle.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '0C4A6E' } };
  forecastTitle.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
  forecastTitle.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } };
  wsForecast.addRow([`Time Range: ${rangeLabel}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  wsForecast.addRow([]);

  const forecastHeader = wsForecast.addRow(['Date', 'Forecast Value', 'Confidence Lower', 'Confidence Upper']);
  forecastHeader.eachCell((cell) => {
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0284C7' } };
  });
  const forecast = enterprise.forecasting.revenueForecast ?? [];
  forecast.forEach((f: ForecastData, i: number) => {
    const row = wsForecast.addRow([
      f.date,
      f.value,
      f.confidenceLower ?? f.value,
      f.confidenceUpper ?? f.value,
    ]);
    [2, 3, 4].forEach((col) => {
      row.getCell(col).numFmt = '"EUR "#,##0.00';
      row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0F9FF' } };
    });
  });

  if (forecast.length === 0) {
    wsForecast.addRow(['Insufficient data for forecasting']).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `analytics_report_${timeRange.value}_${dateStr}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Export analytics data to CSV (legacy)
 */
export function exportAnalyticsToCSV(
  overview: OverviewAnalytics,
  enterprise: EnterpriseAnalytics,
  timeRange: TimeRange
): void {
  const content = [
    `"ENTERPRISE ANALYTICS REPORT"`,
    `"Generated: ${new Date().toLocaleString()}"`,
    `"Time Range: ${timeRange.label}"`,
    '',
    '',
    '"OVERVIEW METRICS"',
    `"Total Inventory Value","${formatCurrency(overview.inventory.totalValue)}"`,
    `"Total Items","${overview.inventory.totalItems}"`,
    `"Total Quantity","${overview.inventory.totalQuantity}"`,
    `"Total Revenue","${formatCurrency(overview.sales.totalRevenue)}"`,
    `"Total Orders","${overview.sales.totalOrders}"`,
    `"Average Order Value","${formatCurrency(overview.sales.avgOrderValue)}"`,
    '',
    '',
    '"PROFIT ANALYSIS"',
    `"Gross Profit","${formatCurrency(enterprise.profit.grossProfit)}"`,
    `"Gross Margin","${enterprise.profit.grossMargin.toFixed(2)}%"`,
    `"Net Profit","${formatCurrency(enterprise.profit.netProfit)}"`,
    `"Net Margin","${enterprise.profit.netMargin.toFixed(2)}%"`,
    `"Cost of Goods Sold","${formatCurrency(enterprise.profit.costOfGoodsSold)}"`,
    '',
    '',
    '"PRODUCT PERFORMANCE"',
    '"TOP PRODUCTS"',
    '"Rank","Product Name","Revenue","Profit","Quantity Sold"',
    ...enterprise.products.topProducts.slice(0, 10).map((product, index) =>
      `"${index + 1}","${product.name}","${formatCurrency(product.revenue)}","${formatCurrency(product.profit)}","${product.quantity}"`
    ),
  ].filter(Boolean).join('\n');

  downloadCSV(content, `analytics_report_${timeRange.value}_${new Date().toISOString().split('T')[0]}`);
}

/**
 * Export analytics data to PDF using jsPDF
 */
export function exportAnalyticsToPDF(
  overview: OverviewAnalytics,
  enterprise: EnterpriseAnalytics,
  timeRange: TimeRange,
  _activeTab?: string
): void {
  import('jspdf').then(({ jsPDF }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = 20;
    const lineHeight = 7;
    const sectionGap = 12;

    const rangeLabel = timeRange.label || timeRange.value;

    doc.setFontSize(20);
    doc.setTextColor(12, 74, 110);
    doc.text('Enterprise Analytics Report', margin, y);
    y += 10;

    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
    y += 5;
    doc.text(`Time Range: ${rangeLabel}`, margin, y);
    y += sectionGap;

    doc.setFontSize(14);
    doc.setTextColor(5, 150, 105);
    doc.text('Overview Metrics', margin, y);
    y += lineHeight;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    const overviewRows = [
      ['Online Revenue', formatCurrency(overview.sales.onlineRevenue)],
      ['Shop Revenue', formatCurrency(overview.sales.shopRevenue)],
      ['Combined Revenue', formatCurrency(overview.sales.totalRevenue)],
      ['Avg Order Value', formatCurrency(overview.sales.avgOrderValue)],
      ['Total Orders', String(overview.sales.totalOrders)],
      ['Today/Week/Month Revenue', `${formatCurrency(overview.sales.revToday)} / ${formatCurrency(overview.sales.revWeek)} / ${formatCurrency(overview.sales.revMonth)}`],
      ['Inventory Value', formatCurrency(overview.inventory.totalValue)],
      ['Low Stock', String(overview.inventory.lowStockCount)],
      ['Out of Stock', String(overview.inventory.outOfStockCount)],
    ];
    overviewRows.forEach(([label, val]) => {
      doc.text(`${label}:`, margin, y);
      doc.text(val, margin + 70, y);
      y += lineHeight;
    });
    y += sectionGap;

    doc.setFontSize(14);
    doc.setTextColor(5, 150, 105);
    doc.text('Profit Analysis', margin, y);
    y += lineHeight;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    const profit = enterprise.profit;
    const profitRows = [
      ['Gross Profit', formatCurrency(profit.grossProfit)],
      ['Gross Margin', `${profit.grossMargin.toFixed(2)}%`],
      ['Net Profit', formatCurrency(profit.netProfit)],
      ['Net Margin', `${profit.netMargin.toFixed(2)}%`],
      ['Cost of Goods Sold', formatCurrency(profit.costOfGoodsSold)],
    ];
    profitRows.forEach(([label, val]) => {
      doc.text(`${label}:`, margin, y);
      doc.text(val, margin + 70, y);
      y += lineHeight;
    });
    y += sectionGap;

    doc.setFontSize(14);
    doc.setTextColor(124, 58, 237);
    doc.text('Top Products', margin, y);
    y += lineHeight;
    doc.setFontSize(10);
    enterprise.products.topProducts.slice(0, 10).forEach((p, i) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(`${i + 1}. ${p.name}`, margin, y);
      doc.text(formatCurrency(p.revenue), margin + 100, y);
      doc.text(`Qty: ${p.quantity}`, margin + 140, y);
      y += lineHeight;
    });
    y += sectionGap;

    doc.setFontSize(14);
    doc.setTextColor(100, 116, 139);
    doc.text('Most Unsold Items (All Time)', margin, y);
    y += lineHeight;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    const fullPdfUnsold = getUnsoldItems(overview, overview.sales.soldNamesAll || []);
    fullPdfUnsold.forEach((item, i) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(`${i + 1}. ${item.name}`, margin, y);
      doc.text(`${item.quantity} in stock`, margin + 100, y);
      y += lineHeight;
    });
    y += sectionGap;

    doc.setFontSize(14);
    doc.setTextColor(2, 132, 199);
    doc.text('Revenue Forecast', margin, y);
    y += lineHeight;
    doc.setFontSize(10);
    const forecast = enterprise.forecasting.revenueForecast ?? [];
    forecast.slice(0, 15).forEach((f) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(`${f.date}: ${formatCurrency(f.value)}`, margin, y);
      y += lineHeight;
    });

    doc.save(`analytics_report_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`);
  });
}

// --- Section-specific exports ---

export async function exportAnalyticsOverviewToExcel(overview: OverviewAnalytics, timeRange: TimeRange): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet('Overview', { properties: { tabColor: { argb: '0EA5E9' } }, pageSetup: { orientation: 'portrait' } });
  const rangeLabel = timeRange.label || timeRange.value;

  ws.columns = [{ key: 'a', width: 28 }, { key: 'b', width: 18 }, { key: 'c', width: 18 }, { key: 'd', width: 18 }];
  ws.mergeCells('A1:D1');
  const title = ws.addRow(['ANALYTICS OVERVIEW']);
  title.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '0C4A6E' } };
  title.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F0F9FF' } };
  title.getCell(1).alignment = { horizontal: 'center' };
  ws.addRow([`Time Range: ${rangeLabel}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  ws.addRow([]);

  const addSection = (name: string, fillColor: string) => {
    const row = ws.addRow([name]);
    ws.mergeCells(`A${row.number}:D${row.number}`);
    row.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
    row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
    row.getCell(1).alignment = { horizontal: 'center' };
  };

  addSection('Channel Revenue', '4F46E5');
  ws.addRow(['Online Revenue', overview.sales.onlineRevenue]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['Shop Revenue', overview.sales.shopRevenue]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['Combined Revenue', overview.sales.totalRevenue]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['Avg Order Value', overview.sales.avgOrderValue]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow([]);

  addSection('Revenue by Period', '059669');
  ws.addRow(['Period', 'Revenue', 'Items Sold', '']).eachCell((c) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D1FAE5' } }; });
  ws.addRow(['Today', overview.sales.revToday, overview.sales.qtyToday, 'items']).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['This Week', overview.sales.revWeek, overview.sales.qtyWeek, 'items']).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['This Month', overview.sales.revMonth, overview.sales.qtyMonth, 'items']).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow([]);

  addSection('Customers Per Day', '0284C7');
  const custHeader = ws.addRow(['Date', 'Online', 'Shop', 'Combined']);
  custHeader.eachCell((c) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } }; });
  (overview.sales.dailyCustomers || []).slice(-14).forEach((d) => {
    ws.addRow([d.date, d.onlineCount, d.shopCount, d.onlineCount + d.shopCount]);
  });
  const onlineTotal = (overview.sales.dailyCustomers || []).reduce((s, d) => s + d.onlineCount, 0);
  const shopTotal = (overview.sales.dailyCustomers || []).reduce((s, d) => s + d.shopCount, 0);
  const combined = onlineTotal + shopTotal;
  const avgDay = (overview.sales.dailyCustomers || []).length > 0 ? Math.round(combined / overview.sales.dailyCustomers.length) : 0;
  ws.addRow(['Summary', onlineTotal, shopTotal, combined]);
  ws.addRow(['Avg/Day', '', '', avgDay]);
  ws.addRow([]);

  addSection('Most Sold Items - Today', 'F59E0B');
  ws.addRow(['Rank', 'Name', 'Quantity', 'Revenue']).eachCell((c) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } }; });
  (overview.sales.topSellingToday || []).slice(0, 10).forEach((item, i) => {
    const row = ws.addRow([i + 1, item.name, item.quantity, item.revenue]);
    row.getCell(4).numFmt = '"EUR "#,##0.00';
  });
  ws.addRow([]);

  addSection('Most Sold Items - Week', 'F59E0B');
  ws.addRow(['Rank', 'Name', 'Quantity', 'Revenue']).eachCell((c) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } }; });
  (overview.sales.topSellingWeek || []).slice(0, 10).forEach((item, i) => {
    const row = ws.addRow([i + 1, item.name, item.quantity, item.revenue]);
    row.getCell(4).numFmt = '"EUR "#,##0.00';
  });
  ws.addRow([]);

  addSection('Most Sold Items - Month', 'F59E0B');
  ws.addRow(['Rank', 'Name', 'Quantity', 'Revenue']).eachCell((c) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } }; });
  (overview.sales.topSellingMonth || []).slice(0, 10).forEach((item, i) => {
    const row = ws.addRow([i + 1, item.name, item.quantity, item.revenue]);
    row.getCell(4).numFmt = '"EUR "#,##0.00';
  });
  ws.addRow([]);

  const unsoldPeriods: Array<{ label: string; soldNames: string[] }> = [
    { label: 'Today', soldNames: overview.sales.soldNamesToday || [] },
    { label: 'Week', soldNames: overview.sales.soldNamesWeek || [] },
    { label: 'Month', soldNames: overview.sales.soldNamesMonth || [] },
    { label: 'All Time', soldNames: overview.sales.soldNamesAll || [] },
  ];
  for (const period of unsoldPeriods) {
    addSection(`Most Unsold Items - ${period.label}`, '64748B');
    ws.addRow(['Rank', 'Name', 'Quantity in Stock']).eachCell((c) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F1F5F9' } }; });
    const unsold = getUnsoldItems(overview, period.soldNames);
    unsold.forEach((item, i) => {
      ws.addRow([i + 1, item.name, item.quantity]);
    });
    ws.addRow([]);
  }

  addSection('Revenue Trend', '047857');
  ws.addRow(['Date', 'Revenue']).eachCell((c) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D1FAE5' } }; });
  (overview.sales.revenueTrend || []).forEach((d) => {
    const row = ws.addRow([d.date, d.value]);
    row.getCell(2).numFmt = '"EUR "#,##0.00';
  });
  ws.addRow([]);

  addSection('Stock Alerts', 'DC2626');
  ws.addRow(['Low Stock', overview.inventory.lowStockCount]);
  ws.addRow(['Out of Stock', overview.inventory.outOfStockCount]);
  ws.addRow([]);

  addSection('Performance KPIs', '7C3AED');
  ws.addRow(['Inventory Turnover', overview.performance.inventoryTurnover]);
  ws.addRow(['Fulfillment Rate (%)', overview.performance.fulfillmentRate]);
  ws.addRow([]);

  addSection('Quick Stats', '0EA5E9');
  ws.addRow(['Categories', overview.inventory.categories]);
  ws.addRow(['Inventory Value', overview.inventory.totalValue]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['Total Stock', overview.inventory.totalQuantity]);
  ws.addRow(['Unique Products', overview.inventory.totalItems]);

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(buffer, `analytics_overview_${timeRange.value}_${new Date().toISOString().split('T')[0]}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

export function exportAnalyticsOverviewToPDF(overview: OverviewAnalytics, timeRange: TimeRange): void {
  import('jspdf').then(({ jsPDF }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 15;
    let y = 20;
    const lh = 7;
    const rangeLabel = timeRange.label || timeRange.value;

    doc.setFontSize(18);
    doc.setTextColor(12, 74, 110);
    doc.text('Analytics Overview', margin, y);
    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Time Range: ${rangeLabel}`, margin, y);
    y += 12;

    doc.setFontSize(12);
    doc.setTextColor(79, 70, 229);
    doc.text('Channel Revenue', margin, y);
    y += lh;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    [
      ['Online Revenue', formatCurrency(overview.sales.onlineRevenue)],
      ['Shop Revenue', formatCurrency(overview.sales.shopRevenue)],
      ['Combined Revenue', formatCurrency(overview.sales.totalRevenue)],
      ['Avg Order Value', formatCurrency(overview.sales.avgOrderValue)],
    ].forEach(([l, v]) => { doc.text(`${l}: ${v}`, margin, y); y += lh; });
    y += 4;

    doc.setFontSize(12);
    doc.setTextColor(5, 150, 105);
    doc.text('Revenue by Period', margin, y);
    y += lh;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    [
      ['Today', formatCurrency(overview.sales.revToday), `${overview.sales.qtyToday} items`],
      ['This Week', formatCurrency(overview.sales.revWeek), `${overview.sales.qtyWeek} items`],
      ['This Month', formatCurrency(overview.sales.revMonth), `${overview.sales.qtyMonth} items`],
    ].forEach(([l, v, sub]) => { doc.text(`${l}: ${v} (${sub})`, margin, y); y += lh; });
    y += 4;

    doc.setFontSize(12);
    doc.setTextColor(2, 132, 199);
    doc.text('Customers Per Day', margin, y);
    y += lh;
    doc.setFontSize(10);
    const dc = overview.sales.dailyCustomers || [];
    const onlineTotal = dc.reduce((s, d) => s + d.onlineCount, 0);
    const shopTotal = dc.reduce((s, d) => s + d.shopCount, 0);
    doc.text(`Online Total: ${onlineTotal} | Shop Total: ${shopTotal} | Combined: ${onlineTotal + shopTotal}`, margin, y);
    y += lh + 4;

    doc.setFontSize(12);
    doc.setTextColor(245, 158, 11);
    doc.text('Most Sold Items', margin, y);
    y += lh;
    doc.setFontSize(10);
    ['topSellingToday', 'topSellingWeek', 'topSellingMonth'].forEach((key, idx) => {
      const items = (overview.sales as any)[key] || [];
      const label = ['Today', 'Week', 'Month'][idx];
      doc.text(`${label}:`, margin, y);
      y += lh;
      items.slice(0, 10).forEach((item: { name: string; quantity: number; revenue: number }, i: number) => {
        doc.text(`  ${i + 1}. ${item.name} - ${item.quantity} sold, ${formatCurrency(item.revenue)}`, margin, y);
        y += lh;
      });
      y += 2;
    });

    doc.setFontSize(12);
    doc.setTextColor(100, 116, 139);
    doc.text('Most Unsold Items', margin, y);
    y += lh;
    doc.setFontSize(10);
    const pdfUnsoldPeriods = [
      { label: 'Today', soldNames: overview.sales.soldNamesToday || [] },
      { label: 'Week', soldNames: overview.sales.soldNamesWeek || [] },
      { label: 'Month', soldNames: overview.sales.soldNamesMonth || [] },
      { label: 'All Time', soldNames: overview.sales.soldNamesAll || [] },
    ];
    for (const period of pdfUnsoldPeriods) {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.text(`${period.label}:`, margin, y);
      y += lh;
      const unsold = getUnsoldItems(overview, period.soldNames);
      unsold.forEach((item, i) => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(`  ${i + 1}. ${item.name} - ${item.quantity} in stock`, margin, y);
        y += lh;
      });
      y += 2;
    }

    doc.setFontSize(12);
    doc.setTextColor(220, 38, 38);
    doc.text('Stock Alerts', margin, y);
    y += lh;
    doc.setFontSize(10);
    doc.text(`Low Stock: ${overview.inventory.lowStockCount} | Out of Stock: ${overview.inventory.outOfStockCount}`, margin, y);
    y += lh + 4;

    doc.setFontSize(12);
    doc.setTextColor(124, 58, 237);
    doc.text('Quick Stats', margin, y);
    y += lh;
    doc.setFontSize(10);
    doc.text(`Categories: ${overview.inventory.categories} | Inventory Value: ${formatCurrency(overview.inventory.totalValue)}`, margin, y);
    y += lh;
    doc.text(`Total Stock: ${overview.inventory.totalQuantity} | Unique Products: ${overview.inventory.totalItems}`, margin, y);

    doc.save(`analytics_overview_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`);
  });
}

export async function exportAnalyticsProfitToExcel(profit: EnterpriseAnalytics['profit'], timeRange: TimeRange): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet('Profit', { properties: { tabColor: { argb: '059669' } }, pageSetup: { orientation: 'portrait' } });
  const rangeLabel = timeRange.label || timeRange.value;

  ws.columns = [{ key: 'metric', width: 32 }, { key: 'value', width: 18 }];
  ws.mergeCells('A1:B1');
  const title = ws.addRow(['PROFIT ANALYSIS']);
  title.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '065F46' } };
  title.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D1FAE5' } };
  title.getCell(1).alignment = { horizontal: 'center' };
  ws.addRow([`Time Range: ${rangeLabel}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  ws.addRow([]);

  const sectionHeader = (name: string, color: string) => {
    const row = ws.addRow([name]);
    ws.mergeCells(`A${row.number}:B${row.number}`);
    row.getCell(1).font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFF' } };
    row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
  };

  sectionHeader('Key Metrics', '059669');
  [
    ['Gross Profit', profit.grossProfit],
    ['Gross Margin (%)', profit.grossMargin],
    ['Net Profit', profit.netProfit],
    ['Net Margin (%)', profit.netMargin],
    ['Cost of Goods Sold', profit.costOfGoodsSold],
  ].forEach(([m, v], i) => {
    const row = ws.addRow([m, v]);
    row.getCell(2).numFmt = String(m).includes('Margin') ? '0.00' : '"EUR "#,##0.00';
    row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0FDF4' } }; });
  });
  ws.addRow([]);

  sectionHeader('Profit Breakdown', '047857');
  const revenue = profit.grossProfit + profit.costOfGoodsSold;
  ws.addRow(['Revenue', revenue]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['Cost of Goods Sold', -profit.costOfGoodsSold]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['Gross Profit', profit.grossProfit]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow([]);

  sectionHeader('Margin Analysis', '0369A1');
  ws.addRow(['Gross Margin', `${profit.grossMargin.toFixed(1)}%`]);
  ws.addRow(['Period Change', profit.trends?.grossProfit?.change ?? 0]).getCell(2).numFmt = '"EUR "#,##0.00';
  const status = profit.grossMargin > 40 ? 'Excellent' : profit.grossMargin > 20 ? 'Good' : 'Needs Improvement';
  ws.addRow(['Profitability Status', status]);
  ws.addRow([]);

  if (profit.profitTrend && profit.profitTrend.length > 0) {
    sectionHeader('Daily Profit Trend', '047857');
    ws.addRow(['Date', 'Daily Profit']).eachCell((c) => {
      c.font = { bold: true, color: { argb: 'FFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '065F46' } };
    });
    profit.profitTrend.forEach((pt, i) => {
      const row = ws.addRow([pt.date, pt.value]);
      row.getCell(2).numFmt = '"EUR "#,##0.00';
      row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0FDF4' } }; });
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(buffer, `analytics_profit_${timeRange.value}_${new Date().toISOString().split('T')[0]}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

export function exportAnalyticsProfitToPDF(profit: EnterpriseAnalytics['profit'], timeRange: TimeRange): void {
  import('jspdf').then(({ jsPDF }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 15;
    let y = 20;
    const lh = 7;

    doc.setFontSize(18);
    doc.setTextColor(5, 150, 105);
    doc.text('Profit Analysis', margin, y);
    y += 10;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    [
      ['Gross Profit', formatCurrency(profit.grossProfit)],
      ['Gross Margin', `${profit.grossMargin.toFixed(2)}%`],
      ['Net Profit', formatCurrency(profit.netProfit)],
      ['Cost of Goods Sold', formatCurrency(profit.costOfGoodsSold)],
    ].forEach(([l, v]) => { doc.text(`${l}: ${v}`, margin, y); y += lh; });
    y += 6;

    doc.setFontSize(12);
    doc.setTextColor(5, 150, 105);
    doc.text('Profit Breakdown', margin, y);
    y += lh;
    doc.setFontSize(10);
    const revenue = profit.grossProfit + profit.costOfGoodsSold;
    doc.text(`Revenue: ${formatCurrency(revenue)}`, margin, y); y += lh;
    doc.setTextColor(220, 38, 38);
    doc.text(`COGS: -${formatCurrency(profit.costOfGoodsSold)}`, margin, y); y += lh;
    doc.setTextColor(5, 150, 105);
    doc.text(`Gross Profit: ${formatCurrency(profit.grossProfit)}`, margin, y); y += lh + 4;

    doc.setFontSize(12);
    doc.text('Margin Analysis', margin, y);
    y += lh;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    const status = profit.grossMargin > 40 ? 'Excellent' : profit.grossMargin > 20 ? 'Good' : 'Needs Improvement';
    doc.text(`Profitability Status: ${status}`, margin, y);

    doc.save(`analytics_profit_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`);
  });
}

export async function exportAnalyticsProductsToExcel(products: EnterpriseAnalytics['products'], timeRange: TimeRange): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet('Products', { properties: { tabColor: { argb: '7C3AED' } }, pageSetup: { orientation: 'landscape' } });
  const rangeLabel = timeRange.label || timeRange.value;

  ws.columns = [
    { key: 'rank', width: 6 },
    { key: 'name', width: 32 },
    { key: 'revenue', width: 14 },
    { key: 'profit', width: 14 },
    { key: 'quantity', width: 10 },
    { key: 'color', width: 12 },
    { key: 'size', width: 10 },
  ];
  ws.mergeCells('A1:G1');
  const title = ws.addRow(['PRODUCT PERFORMANCE']);
  title.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '5B21B6' } };
  title.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EDE9FE' } };
  title.getCell(1).alignment = { horizontal: 'center' };
  ws.addRow([`Time Range: ${rangeLabel}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  ws.addRow([]);

  const sectionHeader = (name: string, color: string) => {
    const row = ws.addRow([name]);
    ws.mergeCells(`A${row.number}:G${row.number}`);
    row.getCell(1).font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
    row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
    row.getCell(1).alignment = { horizontal: 'center' };
  };

  sectionHeader('Top 10 Products by Revenue', '059669');
  const prodCols = ['Rank', 'Product Name', 'Revenue', 'Profit', 'Quantity', 'Color', 'Size'];
  ws.addRow(prodCols).eachCell((c) => {
    c.font = { bold: true, color: { argb: 'FFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '7C3AED' } };
  });
  products.topProducts.slice(0, 10).forEach((p, i) => {
    const row = ws.addRow([i + 1, p.name, p.revenue, p.profit, p.quantity, p.colorName || '-', p.sizeName || '-']);
    row.getCell(3).numFmt = '"EUR "#,##0.00';
    row.getCell(4).numFmt = '"EUR "#,##0.00';
    row.eachCell((c, col) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F5F3FF' } }; });
  });
  ws.addRow([]);

  sectionHeader('Bottom 10 Products', 'DC2626');
  ws.addRow(prodCols).eachCell((c) => {
    c.font = { bold: true, color: { argb: 'FFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'B91C1C' } };
  });
  products.bottomProducts.slice(0, 10).forEach((p, i) => {
    const row = ws.addRow([i + 1, p.name, p.revenue, p.profit, p.quantity, p.colorName || '-', p.sizeName || '-']);
    row.getCell(3).numFmt = '"EUR "#,##0.00';
    row.getCell(4).numFmt = '"EUR "#,##0.00';
    row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'FEF2F2' } }; });
  });
  ws.addRow([]);

  sectionHeader('ABC Analysis (80/20 Rule)', '0369A1');
  ws.addRow(['Category', 'Product Name', 'Value', 'Percentage']).eachCell((c) => {
    c.font = { bold: true, color: { argb: 'FFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0284C7' } };
  });
  const abcColors = { A: '059669', B: 'D97706', C: '64748B' };
  for (const cat of ['A', 'B', 'C'] as const) {
    (products.abcAnalysis[cat]?.products ?? []).forEach((p, i) => {
      const row = ws.addRow([cat, p.name, p.value, p.percentage]);
      row.getCell(3).numFmt = '"EUR "#,##0.00';
      row.getCell(4).numFmt = '0.00%';
      row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: abcColors[cat] } };
      row.getCell(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      row.eachCell((c, col) => { if (col > 1) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0F9FF' } }; });
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(buffer, `analytics_products_${timeRange.value}_${new Date().toISOString().split('T')[0]}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

export function exportAnalyticsProductsToPDF(products: EnterpriseAnalytics['products'], timeRange: TimeRange): void {
  import('jspdf').then(({ jsPDF }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 15;
    let y = 20;
    const lh = 7;

    doc.setFontSize(18);
    doc.setTextColor(124, 58, 237);
    doc.text('Product Performance', margin, y);
    y += 10;

    doc.setFontSize(12);
    doc.setTextColor(5, 150, 105);
    doc.text('Top 10 Products', margin, y);
    y += lh;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    products.topProducts.slice(0, 10).forEach((p, i) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(`${i + 1}. ${p.name} - ${formatCurrency(p.revenue)} | Profit: ${formatCurrency(p.profit)} | Qty: ${p.quantity}`, margin, y);
      y += lh;
    });
    y += 6;

    doc.setFontSize(12);
    doc.setTextColor(220, 38, 38);
    doc.text('Bottom 10 Products', margin, y);
    y += lh;
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    products.bottomProducts.slice(0, 10).forEach((p, i) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(`${i + 1}. ${p.name} - ${formatCurrency(p.revenue)} | Qty: ${p.quantity}`, margin, y);
      y += lh;
    });
    y += 6;

    doc.setFontSize(12);
    doc.setTextColor(2, 132, 199);
    doc.text('ABC Analysis', margin, y);
    y += lh;
    doc.setFontSize(10);
    for (const cat of ['A', 'B', 'C'] as const) {
      const prods = products.abcAnalysis[cat]?.products ?? [];
      doc.text(`Category ${cat} (${prods.length} products):`, margin, y);
      y += lh;
      prods.slice(0, 5).forEach((p) => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(`  - ${p.name} (${p.percentage.toFixed(1)}%)`, margin, y);
        y += lh;
      });
      y += 2;
    }

    doc.save(`analytics_products_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`);
  });
}

export async function exportAnalyticsForecastingToExcel(
  forecasting: EnterpriseAnalytics['forecasting'],
  timeRange: TimeRange,
  historicalData?: Array<{ date: string; value: number }>
): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet('Forecasting', { properties: { tabColor: { argb: '0284C7' } }, pageSetup: { orientation: 'portrait' } });
  const rangeLabel = timeRange.label || timeRange.value;
  const forecast = forecasting.revenueForecast ?? [];

  ws.columns = [
    { key: 'date', width: 14 },
    { key: 'value', width: 16 },
    { key: 'lower', width: 16 },
    { key: 'upper', width: 16 },
  ];
  ws.mergeCells('A1:D1');
  const title = ws.addRow(['REVENUE FORECAST']);
  title.getCell(1).font = { name: 'Arial', size: 18, bold: true, color: { argb: '0C4A6E' } };
  title.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0F2FE' } };
  title.getCell(1).alignment = { horizontal: 'center' };
  ws.addRow([`Time Range: ${rangeLabel}`]).getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
  ws.addRow([]);

  if (historicalData && historicalData.length > 0) {
    const histHeader = ws.addRow(['Historical Data']);
    ws.mergeCells(`A${histHeader.number}:D${histHeader.number}`);
    histHeader.getCell(1).font = { bold: true, color: { argb: 'FFFFFF' } };
    histHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '059669' } };
    ws.addRow(['Date', 'Revenue', 'Confidence Lower', 'Confidence Upper']).eachCell((c) => {
      c.font = { bold: true, color: { argb: 'FFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '047857' } };
    });
    historicalData.forEach((d, i) => {
      const row = ws.addRow([d.date, d.value, d.value, d.value]);
      [2, 3, 4].forEach((col) => { row.getCell(col).numFmt = '"EUR "#,##0.00'; row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0FDF4' } }; });
    });
    ws.addRow([]);
  }

  const fcastHeader = ws.addRow(['Forecast Data']);
  ws.mergeCells(`A${fcastHeader.number}:D${fcastHeader.number}`);
  fcastHeader.getCell(1).font = { bold: true, color: { argb: 'FFFFFF' } };
  fcastHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0284C7' } };
  ws.addRow(['Date', 'Forecast Value', 'Confidence Lower', 'Confidence Upper']).eachCell((c) => {
    c.font = { bold: true, color: { argb: 'FFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0369A1' } };
  });
  forecast.forEach((f: ForecastData, i: number) => {
    const row = ws.addRow([f.date, f.value, f.confidenceLower ?? f.value, f.confidenceUpper ?? f.value]);
    [2, 3, 4].forEach((col) => {
      row.getCell(col).numFmt = '"EUR "#,##0.00';
      row.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: i % 2 === 0 ? 'FFFFFF' : 'F0F9FF' } };
    });
  });
  ws.addRow([]);

  const next7 = forecast.slice(0, 7).reduce((s, f) => s + f.value, 0);
  const next30 = forecast.reduce((s, f) => s + f.value, 0);
  const avgDaily = forecast.length > 0 ? next30 / forecast.length : 0;
  const summaryHeader = ws.addRow(['Summary']);
  ws.mergeCells(`A${summaryHeader.number}:D${summaryHeader.number}`);
  summaryHeader.getCell(1).font = { bold: true, color: { argb: 'FFFFFF' } };
  summaryHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0EA5E9' } };
  ws.addRow(['Next 7 Days', next7]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['Next 30 Days', next30]).getCell(2).numFmt = '"EUR "#,##0.00';
  ws.addRow(['Avg Daily', avgDaily]).getCell(2).numFmt = '"EUR "#,##0.00';

  if (forecast.length === 0) {
    ws.addRow(['Insufficient data for forecasting']).getCell(1).font = { italic: true, color: { argb: '64748B' } };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(buffer, `analytics_forecasting_${timeRange.value}_${new Date().toISOString().split('T')[0]}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

export function exportAnalyticsForecastingToPDF(
  forecasting: EnterpriseAnalytics['forecasting'],
  timeRange: TimeRange,
  historicalData?: Array<{ date: string; value: number }>
): void {
  import('jspdf').then(({ jsPDF }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 15;
    let y = 20;
    const lh = 7;
    const forecast = forecasting.revenueForecast ?? [];

    doc.setFontSize(18);
    doc.setTextColor(2, 132, 199);
    doc.text('Revenue Forecast', margin, y);
    y += 10;

    doc.setFontSize(12);
    doc.setTextColor(51, 65, 85);
    doc.text('Summary', margin, y);
    y += lh;
    doc.setFontSize(10);
    const next7 = forecast.slice(0, 7).reduce((s, f) => s + f.value, 0);
    const next30 = forecast.reduce((s, f) => s + f.value, 0);
    const avgDaily = forecast.length > 0 ? next30 / forecast.length : 0;
    doc.text(`Next 7 Days: ${formatCurrency(next7)}`, margin, y); y += lh;
    doc.text(`Next 30 Days: ${formatCurrency(next30)}`, margin, y); y += lh;
    doc.text(`Avg Daily: ${formatCurrency(avgDaily)}`, margin, y); y += 10;

    doc.setFontSize(12);
    doc.text('Forecast Data', margin, y);
    y += lh;
    doc.setFontSize(10);
    forecast.slice(0, 25).forEach((f: ForecastData) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(`${f.date}: ${formatCurrency(f.value)} (range: ${formatCurrency(f.confidenceLower ?? f.value)} - ${formatCurrency(f.confidenceUpper ?? f.value)})`, margin, y);
      y += lh;
    });

    doc.save(`analytics_forecasting_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`);
  });
}

function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function downloadBlob(buffer: ArrayBuffer, filename: string, mimeType: string): void {
  const blob = new Blob([buffer], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
